/**
 * Builds the per-device sensor/climate data for the HyDry sensor-data Excel
 * export (IRES Admin Client: Mandanten > client > Geräte > Sensordaten-Export).
 *
 * One worksheet per device, concatenating every run the device was assigned to
 * the chosen client for, restricted to a start/end date window. Two device
 * families are supported, each with its own column set:
 *
 * - **ComBox (HyDry Box):** every *connected* sensor (not just the room sensor).
 *   Temperature/humidity sensors contribute `<label> Tu`/`<label> Fu`, material
 *   sensors contribute `<label> R` (resistance digits), radon sensors contribute
 *   `<label> Radon`. Labels follow the UI slot scheme (`1L`, `2S`, …; the room
 *   sensor is `Raum`).
 * - **Panel:** the measurement fields the panel sample list shows — wall
 *   temperature, chip temperature, power, heat-ratio % (intensity samples), and
 *   for edge panels the second wall/chip temperature pair. Panels have no
 *   sensor slots or humidity.
 *
 * Column headers stay non-localized (they are data, matching the `Tu`/`Fu`
 * notebook convention of the original room-climate export).
 *
 * The pure decode/format helpers here are unit-tested; `buildDeviceSensorSheet`
 * orchestrates the Firestore reads.
 */
import { createComBoxSampleFromBase64 } from "@/data/models/SampleInfo";
import {
  ComBoxSensorType,
  ROOM_SENSOR_INDEX,
} from "@/data/models/ComBoxSensorData";
import {
  aggregateSensorSlotSerials,
  formatSensorSlotLabel,
  getSerialForSensorIndex,
  isHyDryMiniSerial,
} from "@/data/models/ComBox";
import { getPanelTypeFromSerialNumber, PanelType } from "@/data/models/Panel";
import {
  IresDeviceCategory,
  getIresDeviceCategoryFromSerialNumber,
} from "@/data/models/IresDevice";
import { RunOverview } from "@/data/models/RunOverview";
import { IresDeviceAssignment } from "@/data/models/IresDeviceAssignment";
import {
  RawSampleData,
  loadAllRuns,
  loadSamplesOfRun,
} from "@/data/repository/RunRepository";
import { getAllAssignmentsOfCurrentClient } from "@/data/repository/IresDeviceAssignmentRepository";
import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import { hexToUint8Array } from "@/utils/ByteUtils";
import { formatDate } from "@/utils/DateFormatter";
import { ExcelColumnSpec, ExcelSheetSpec } from "@/utils/DeviceExportFormats";

/** Seconds of the maximum Firestore Timestamp (year 9999): open-ended assignment end. */
const OPEN_ENDED_ASSIGNMENT_END_SECONDS = 253402300799;

/** Excel column-number date format used for the timestamp column. */
const TIMESTAMP_NUM_FMT = "dd.mm.yyyy hh:mm";
const TIMESTAMP_HEADER = "Timestamp";

/** Placeholder for cells where a sensor had no reading at that timestamp. */
const NO_VALUE_PLACEHOLDER = "-";

/** Sentinel byte (0xFF) meaning "no reading" for panel temperature bytes. */
const NO_READING_BYTE = 255;

/** Panel sample events that carry a normal reading (0) or an intensity report (18). */
const PANEL_MEASUREMENT_EVENT_NORMAL = 0;
const PANEL_MEASUREMENT_EVENT_INTENSITY = 18;

/** Characters Excel forbids in worksheet names, plus the 31-char length limit. */
const EXCEL_FORBIDDEN_SHEET_CHARS = /[\\/?*[\]:]/g;
const MAX_SHEET_NAME_LENGTH = 31;

/** A dynamic export column: `key` maps a {@link SensorReadingRow} value to a header. */
export interface SensorColumn {
  key: string;
  header: string;
  width?: number;
}

/** A single decoded reading: a timestamp plus one numeric value per populated column. */
export interface SensorReadingRow {
  timestamp: Date;
  values: Record<string, number>;
}

// --- ComBox column keys -----------------------------------------------------

type ComBoxSensorKind = "temp" | "hum" | "res" | "radon";

function comBoxColumnKey(sensorIndex: number, kind: ComBoxSensorKind): string {
  return `c${sensorIndex}_${kind}`;
}

/** Which value kinds a ComBox sensor of the given type contributes to the export. */
function comBoxSensorKinds(sensorType: ComBoxSensorType): ComBoxSensorKind[] {
  switch (sensorType) {
    case ComBoxSensorType.TELAIRE:
    case ComBoxSensorType.SHT3X:
    case ComBoxSensorType.BLE_SENSOR_TYPE_1:
    case ComBoxSensorType.BLE_SENSOR_TYPE_5:
      return ["temp", "hum"];
    case ComBoxSensorType.BLE_SENSOR_TYPE_3:
    case ComBoxSensorType.BLE_SENSOR_TYPE_MATERIAL:
      return ["res"];
    case ComBoxSensorType.RADON:
      return ["radon"];
    default:
      return [];
  }
}

function comBoxColumnHeader(label: string, kind: ComBoxSensorKind): string {
  switch (kind) {
    case "temp":
      return `${label} Tu`;
    case "hum":
      return `${label} Fu`;
    case "res":
      return `${label} R`;
    case "radon":
      return `${label} Radon`;
  }
}

// --- Panel column keys ------------------------------------------------------

const PANEL_WALL_TEMP_KEY = "wallTemp";
const PANEL_CHIP_TEMP_KEY = "chipTemp";
const PANEL_POWER_KEY = "power";
const PANEL_HEAT_RATIO_KEY = "heatRatio";
const PANEL_WALL_TEMP_2_KEY = "wallTemp2";
const PANEL_CHIP_TEMP_2_KEY = "chipTemp2";

/**
 * Whether a run falls within one of the given device-assignment windows, i.e.
 * the device was assigned to the chosen client for the whole run. Mirrors the
 * per-run rule in `matchesDeviceAssignmentDetails` (ProgramChain.ts) so the
 * export includes exactly the runs a user sees in Drying History. Open-ended
 * assignments (`endDate === null`) extend to the max Timestamp.
 */
export function runBelongsToClient(
  run: RunOverview,
  assignments: IresDeviceAssignment[]
): boolean {
  return assignments.some((assignment) => {
    const assignmentEndSeconds = assignment.endDate
      ? assignment.endDate.seconds
      : OPEN_ENDED_ASSIGNMENT_END_SECONDS;

    return (
      run.startDate.seconds >= assignment.startDate.seconds &&
      run.endDate.seconds <= assignmentEndSeconds
    );
  });
}

/**
 * Produces a valid, unique Excel worksheet name from a raw name.
 *
 * Strips Excel-forbidden characters, clamps to 31 characters, and appends a
 * numeric suffix on collision. `usedNames` is mutated to record the result so
 * repeated calls stay unique within one workbook.
 */
export function sanitizeSheetName(
  rawName: string,
  usedNames: Set<string>
): string {
  let name = rawName.replace(EXCEL_FORBIDDEN_SHEET_CHARS, " ").trim();

  if (name.length === 0) {
    name = "Sheet";
  }

  if (name.length > MAX_SHEET_NAME_LENGTH) {
    name = name.slice(0, MAX_SHEET_NAME_LENGTH).trim();
  }

  let uniqueName = name;
  let counter = 2;

  while (usedNames.has(uniqueName)) {
    const suffix = ` (${counter})`;
    uniqueName =
      name.slice(0, MAX_SHEET_NAME_LENGTH - suffix.length).trim() + suffix;
    counter++;
  }

  usedNames.add(uniqueName);
  return uniqueName;
}

/**
 * Builds the ComBox export columns as the union of every *connected* sensor
 * across the given runs. The room sensor is labelled `Raum`; floor sensors use
 * the UI slot label (`1L`, `2S`, …). Last-seen sensor type per slot wins.
 */
export function buildComBoxColumns(
  runs: RunOverview[],
  serialNumber: string
): SensorColumn[] {
  const isMini = isHyDryMiniSerial(serialNumber);
  const serials = aggregateSensorSlotSerials(runs);

  const typeByIndex = new Map<number, ComBoxSensorType>();

  for (const run of runs) {
    const config = run.sensorConfig;

    if (!config) {
      continue;
    }

    config.forEach((sensorType, sensorIndex) => {
      if (
        sensorType !== ComBoxSensorType.EMPTY &&
        sensorType !== ComBoxSensorType.UNKNOWN
      ) {
        typeByIndex.set(sensorIndex, sensorType);
      }
    });
  }

  const columns: SensorColumn[] = [];

  // Room sensor first, then the remaining sensors by ascending index.
  const sortedIndices = [...typeByIndex.keys()].sort((a, b) => {
    if (a === ROOM_SENSOR_INDEX) return -1;
    if (b === ROOM_SENSOR_INDEX) return 1;
    return a - b;
  });

  for (const sensorIndex of sortedIndices) {
    const sensorType = typeByIndex.get(sensorIndex) as ComBoxSensorType;

    const label =
      sensorIndex === ROOM_SENSOR_INDEX
        ? "Raum"
        : formatSensorSlotLabel(
            sensorIndex,
            sensorType,
            getSerialForSensorIndex(serials, sensorIndex),
            isMini
          ) ?? `S${sensorIndex + 1}`;

    for (const kind of comBoxSensorKinds(sensorType)) {
      columns.push({
        key: comBoxColumnKey(sensorIndex, kind),
        header: comBoxColumnHeader(label, kind),
        width: 10,
      });
    }
  }

  return columns;
}

/** The fixed panel export columns (edge panels add a second wall/chip pair). */
export function buildPanelColumns(isEdgePanel: boolean): SensorColumn[] {
  const columns: SensorColumn[] = [
    { key: PANEL_WALL_TEMP_KEY, header: "WallTemp", width: 10 },
    { key: PANEL_CHIP_TEMP_KEY, header: "ChipTemp", width: 10 },
    { key: PANEL_POWER_KEY, header: "Power", width: 10 },
    { key: PANEL_HEAT_RATIO_KEY, header: "HeatRatio%", width: 12 },
  ];

  if (isEdgePanel) {
    columns.push({ key: PANEL_WALL_TEMP_2_KEY, header: "WallTemp2", width: 10 });
    columns.push({ key: PANEL_CHIP_TEMP_2_KEY, header: "ChipTemp2", width: 10 });
  }

  return columns;
}

/**
 * Decodes a ComBox run's raw samples into export rows, one row per sample that
 * carries at least one sensor reading. Reads **all** connected sensors (not just
 * the room sensor); special-event samples (voltage, warnings, config, pause …)
 * carry no readings and are dropped. Only samples inside `[startMillis,
 * endMillis]` are kept.
 */
export function extractComBoxSensorRows(
  run: RunOverview,
  rawSamples: RawSampleData[],
  startMillis: number,
  endMillis: number
): SensorReadingRow[] {
  // Runs without a sensor config cannot be decoded (mirrors the sample list view).
  if (!run.sensorConfig) {
    return [];
  }

  const runStartMillis = run.startDate.toMillis();
  const rows: SensorReadingRow[] = [];

  for (const raw of rawSamples) {
    const sample = createComBoxSampleFromBase64(raw.encodedSample, run, true);

    if (!sample) {
      continue;
    }

    const timestampMillis = runStartMillis + sample.sampleTimeInMinutes * 60000;

    if (timestampMillis < startMillis || timestampMillis > endMillis) {
      continue;
    }

    const values: Record<string, number> = {};

    for (const sensor of sample.sensorData) {
      const index = sensor.sensorIndex;

      if (sensor.temperature !== undefined) {
        values[comBoxColumnKey(index, "temp")] = sensor.temperature;
      }
      if (sensor.relativeHumidity !== undefined) {
        values[comBoxColumnKey(index, "hum")] = sensor.relativeHumidity;
      }
      if (sensor.resistanceDigits !== undefined) {
        values[comBoxColumnKey(index, "res")] = sensor.resistanceDigits;
      }
      if (sensor.radonValue !== undefined) {
        values[comBoxColumnKey(index, "radon")] = sensor.radonValue;
      }
    }

    if (Object.keys(values).length === 0) {
      continue;
    }

    rows.push({ timestamp: new Date(timestampMillis), values });
  }

  return rows;
}

/** Little-endian uint16 read matching the panel sample decode. */
function readPanelUint16(reader: IresDeviceResponseReader): number {
  const value =
    reader.data[reader.currentIndex] + reader.data[reader.currentIndex + 1] * 256;
  reader.skip(2);
  return value;
}

/**
 * Decodes a Panel run's raw samples into export rows, mirroring the verified
 * hex-`samplePages` decode used by the panel sample list
 * (`IresDeviceSampleList.displaySampleDataOfPanel`). Keeps only measurement
 * samples (normal / intensity events), de-duplicates by sample index, and
 * blanks the 0xFF "no reading" sentinel. Heat-ratio % is only reported on
 * intensity samples (the only samples where that byte is a heat ratio). Only
 * samples inside `[startMillis, endMillis]` are kept.
 */
export function extractPanelSensorRows(
  run: RunOverview,
  rawSamples: RawSampleData[],
  startMillis: number,
  endMillis: number,
  isEdgePanel: boolean
): SensorReadingRow[] {
  const reader = new IresDeviceResponseReader();
  const runStartMillis = run.startDate.toMillis();
  const rows: SensorReadingRow[] = [];

  // The device resets its time counter to a non-zero base; the sample-index-0
  // sample carries that base, subtracted from every sample's time delta.
  let timeDiffToBeAdjusted = 0;
  let previousSampleIndex = -1;

  for (const raw of rawSamples) {
    reader.setNewData(hexToUint8Array(raw.encodedSample));

    const timeDiffRaw = readPanelUint16(reader);
    const wallTemp = reader.readUint8();
    const chipTemp = reader.readUint8();
    const power = readPanelUint16(reader);
    reader.readUint8(); // stepAndProgram (unused)
    reader.readUint8(); // cycleAndStatus (unused)
    const event = reader.readUint8();
    const sampleIndex = readPanelUint16(reader);

    let wallTemp2: number | undefined;
    let chipTemp2: number | undefined;

    if (isEdgePanel) {
      wallTemp2 = reader.readUint8();
      chipTemp2 = reader.readUint8();
    }

    const heatRatioByte = reader.readUint8();

    if (sampleIndex === 0) {
      timeDiffToBeAdjusted = timeDiffRaw;
    }

    // De-duplicate consecutive samples sharing a sample index (matches the UI).
    const isDuplicate = sampleIndex === previousSampleIndex;
    previousSampleIndex = sampleIndex;

    if (isDuplicate) {
      continue;
    }

    // Keep only measurement samples; drop special-event samples (battery
    // voltage, power reduction, pause info, warnings, …).
    if (
      event !== PANEL_MEASUREMENT_EVENT_NORMAL &&
      event !== PANEL_MEASUREMENT_EVENT_INTENSITY
    ) {
      continue;
    }

    const timestampMillis =
      runStartMillis + (timeDiffRaw - timeDiffToBeAdjusted) * 60000;

    if (timestampMillis < startMillis || timestampMillis > endMillis) {
      continue;
    }

    const values: Record<string, number> = {};

    if (wallTemp !== NO_READING_BYTE) {
      values[PANEL_WALL_TEMP_KEY] = wallTemp;
    }
    if (chipTemp !== NO_READING_BYTE) {
      values[PANEL_CHIP_TEMP_KEY] = chipTemp;
    }
    values[PANEL_POWER_KEY] = power;
    if (event === PANEL_MEASUREMENT_EVENT_INTENSITY) {
      values[PANEL_HEAT_RATIO_KEY] = Math.floor((100 * heatRatioByte) / 255);
    }
    if (isEdgePanel) {
      if (wallTemp2 !== undefined && wallTemp2 !== NO_READING_BYTE) {
        values[PANEL_WALL_TEMP_2_KEY] = wallTemp2;
      }
      if (chipTemp2 !== undefined && chipTemp2 !== NO_READING_BYTE) {
        values[PANEL_CHIP_TEMP_2_KEY] = chipTemp2;
      }
    }

    rows.push({ timestamp: new Date(timestampMillis), values });
  }

  return rows;
}

/**
 * Assembles the export rows + used columns into a ready-to-write worksheet.
 * Columns that stay empty across all rows are dropped so a device only shows
 * the measurements it actually recorded. Returns `null` when there is no data.
 */
export function buildSheetFromRows(
  serialNumber: string,
  deviceLabel: string,
  columns: SensorColumn[],
  rows: SensorReadingRow[],
  usedSheetNames: Set<string>
): ExcelSheetSpec | null {
  if (rows.length === 0) {
    return null;
  }

  rows.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  const usedColumns = columns.filter((column) =>
    rows.some((row) => row.values[column.key] !== undefined)
  );

  if (usedColumns.length === 0) {
    return null;
  }

  const start = rows[0].timestamp;
  const end = rows[rows.length - 1].timestamp;

  const rawName =
    `${deviceLabel} ${serialNumber}`.trim() +
    `_${formatDate(start, "yyyyMMdd")}-${formatDate(end, "yyyyMMdd")}`;

  const columnSpecs: ExcelColumnSpec[] = [
    { header: TIMESTAMP_HEADER, numFmt: TIMESTAMP_NUM_FMT, width: 18 },
    ...usedColumns.map((column) => ({
      header: column.header,
      width: column.width,
    })),
  ];

  return {
    name: sanitizeSheetName(rawName, usedSheetNames),
    columns: columnSpecs,
    rows: rows.map((row) => [
      row.timestamp,
      ...usedColumns.map((column) => row.values[column.key] ?? NO_VALUE_PLACEHOLDER),
    ]),
  };
}

/**
 * Loads every run of an IRES device (ComBox or Panel), decodes its sensor
 * readings, and returns a ready-to-write {@link ExcelSheetSpec} (one worksheet
 * for the device). Returns `null` when the device has no usable data in the
 * window.
 *
 * Runs are processed sequentially to bound memory; the sheet name follows
 * `<label> <serial>_<startYYYYMMDD>-<endYYYYMMDD>`. Only runs the device was
 * assigned to `clientId` for, and that overlap `[startMillis, endMillis]`, are
 * included, so a device that moved between clients never carries another
 * client's data into the file.
 */
export async function buildDeviceSensorSheet(
  clientId: string,
  serialNumber: string,
  deviceLabel: string,
  usedSheetNames: Set<string>,
  startMillis: number,
  endMillis: number
): Promise<ExcelSheetSpec | null> {
  const category = getIresDeviceCategoryFromSerialNumber(serialNumber);

  const [runs, assignments] = await Promise.all([
    loadAllRuns(serialNumber),
    getAllAssignmentsOfCurrentClient(serialNumber, clientId),
  ]);

  const includedRuns = runs.filter((run) => {
    if (!runBelongsToClient(run, assignments)) {
      return false;
    }

    const runStartMillis = run.startDate.toMillis();
    const runEndMillis = run.endDate
      ? run.endDate.toMillis()
      : Number.MAX_SAFE_INTEGER;

    // Keep runs that overlap the selected window.
    return runEndMillis >= startMillis && runStartMillis <= endMillis;
  });

  const isEdgePanel =
    category === IresDeviceCategory.PANEL &&
    getPanelTypeFromSerialNumber(serialNumber) === PanelType.EDGE;

  const columns =
    category === IresDeviceCategory.COMBOX
      ? buildComBoxColumns(includedRuns, serialNumber)
      : buildPanelColumns(isEdgePanel);

  const rows: SensorReadingRow[] = [];

  for (const run of includedRuns) {
    const rawSamples = await loadSamplesOfRun(serialNumber, run.id);

    if (category === IresDeviceCategory.COMBOX) {
      rows.push(
        ...extractComBoxSensorRows(run, rawSamples, startMillis, endMillis)
      );
    } else {
      rows.push(
        ...extractPanelSensorRows(
          run,
          rawSamples,
          startMillis,
          endMillis,
          isEdgePanel
        )
      );
    }
  }

  return buildSheetFromRows(
    serialNumber,
    deviceLabel,
    columns,
    rows,
    usedSheetNames
  );
}
